import { listModules } from "./sim/modules";
import { shipCargoBreakdown, shipCargoQuality, shipTelemetry, totalCargoCapacity } from "./sim/ship";
import { totalCargoMass } from "./sim/tick";
import type { Ship, World } from "./sim/types";

const hud = document.getElementById("hud") as HTMLDivElement;

const catalogSize = listModules().length;

const HUD_INTERVAL_MS = 160;
let _lastHudMs = 0;
const _cargoParts: string[] = [];

function fmtKg(kg: number): string {
  if (kg >= 1_000_000) return `${(kg / 1_000_000).toFixed(2)} kt`;
  if (kg >= 1_000) return `${(kg / 1_000).toFixed(2)} t`;
  return `${kg.toFixed(0)} kg`;
}

function fmtW(w: number): string {
  if (Math.abs(w) >= 1_000_000) return `${(w / 1_000_000).toFixed(1)} MW`;
  if (Math.abs(w) >= 1_000) return `${(w / 1_000).toFixed(1)} kW`;
  return `${w.toFixed(0)} W`;
}

function fmtN(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} MN`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)} kN`;
  return `${n.toFixed(0)} N`;
}

function cargoBreakdown(ship: Ship): string {
  const agg = shipCargoBreakdown(ship);
  if (agg.size === 0) return "";
  _cargoParts.length = 0;
  for (const [resource, kg] of agg) {
    const q = shipCargoQuality(ship, resource);
    _cargoParts.push(`${resource}:${kg.toFixed(0)}kg@${(q * 100).toFixed(0)}%`);
  }
  return "\n      " + _cargoParts.join("  ");
}

export function updateHud(world: World, player: Ship, fps: number): void {
  const now = performance.now();
  if (now - _lastHudMs < HUD_INTERVAL_MS) return;
  _lastHudMs = now;

  const v = Math.hypot(player.body.vel.x, player.body.vel.y);
  const t = shipTelemetry(player);

  const aFwd = t.mass > 0 ? t.thrustForwardN / t.mass : 0;
  const aLat = t.mass > 0 ? t.thrustLateralN / t.mass : 0;

  let loadedAsteroids = 0;
  for (const s of world.ships.values()) if (s.isAsteroid) loadedAsteroids++;
  const totalAsteroids = world.asteroids.length + loadedAsteroids;

  hud.textContent =
`SPACEJUNKER  t=${world.time.toFixed(1)}s  tick=${world.tick}  fps=${fps.toFixed(0)}
ship  ${player.name}  modules=${player.modules.length}/${catalogSize} catalogued
mass  ${fmtKg(t.mass)}        I=${player.body.inertia.toFixed(0)} kg·m²
hp    ${t.hp.toFixed(0)} / ${t.hpMax.toFixed(0)}        crew ${t.crewRequired}
pos   ${player.body.pos.x.toFixed(1)}, ${player.body.pos.y.toFixed(1)}
vel   ${v.toFixed(2)} m/s   rot=${((player.body.rot * 180 / Math.PI) % 360).toFixed(0)}°
thrst ${fmtN(t.thrustForwardN)} fwd  ${fmtN(t.thrustLateralN)} lat  a≈${aFwd.toFixed(1)}/${aLat.toFixed(1)} m/s²
pwr   ${fmtW(t.powerOutW)} out  −${fmtW(t.powerIdleW)} idle  ${fmtW(t.powerBalanceW)} net
batt  ${(t.batteryWh).toFixed(0)} / ${t.batteryCapWh.toFixed(0)} Wh   ${player.powered ? "ONLINE" : "BLACKOUT"}
fuel  ${t.fuelKg.toFixed(0)} / ${t.fuelCapKg.toFixed(0)} kg
heat  ${fmtW(t.heatOutW)} out  ${fmtW(t.heatDissipateW)} rad  Tmax=${t.tempMaxK.toFixed(0)} K  Tavg=${t.tempMeanK.toFixed(0)} K
cargo ${totalCargoMass(player).toFixed(0)} / ${totalCargoCapacity(player).toFixed(0)} kg${cargoBreakdown(player)}
mode  ${player.fireMode.toUpperCase()}    auto ${player.autoBrake ? "ON" : "off"}
proj  ${world.projectiles.size} live    items ${world.items.size} in view
ast   ${totalAsteroids} field  ${loadedAsteroids} loaded (≤2km)
thr   ${player.throttle.toFixed(2)}  steer ${player.steer.toFixed(2)}  strafe ${player.strafe.toFixed(2)}
keys  W thrust  A/D rotate  Q/E strafe  Space fire  Shift mode  B auto-brake`;
}

export function showFatal(msg: string): void {
  const fatal = document.getElementById("fatal");
  const m = document.getElementById("fatal-msg");
  if (fatal && m) {
    m.textContent = msg;
    fatal.classList.add("show");
  }
}
